import { useMemo, useState } from "react";
import {
  IndividualType,
  PlatformSource,
  RecentAnswer,
  getStoreLinkRecord,
} from "winslow-core";
import { getDateDaysAgo } from "winslow-core";
import RatingsChart from "./RatingsChart";
import { ViewToggle } from "./ViewToggle";
import { CollectionStatsQuestions } from "./CollectionStatsQuestions";
import { AskedQuestionsZero } from "../collections/AskedQuestionsZero";
import { SourceSelector } from "../SourceSelector";
import { Selector } from "../common/Selector";
import { useSelector } from "react-redux";
import { selectLinkRecords } from "../../redux/features/appData";

type RatingStatsProps = {
  answers: RecentAnswer[];
  onRatingDelete: () => void;
};

export const RatingStats = ({ answers, onRatingDelete }: RatingStatsProps) => {
  const [view, setView] = useState<"cards" | "list">("cards");
  const [ratingFilter, setRatingFilter] = useState<number | undefined>(
    undefined
  );
  const [raterFilter, setRaterFilter] = useState<string | undefined>(undefined);
  const [sourceFilter, setSourceFilter] = useState<PlatformSource | undefined>(
    undefined
  );
  const linkRecords = useSelector(selectLinkRecords);

  const getRaterFullName = (fr: any): string => {
    if (!fr) return "";
    const ind = fr.user || fr.employee || undefined;
    if (ind) {
      if (fr.individualType === IndividualType.USER) {
        const fn = ind.firstName || "";
        const ln = ind.lastName || "";
        const nm = `${fn} ${ln}`.trim();
        return nm || ind.email || "";
      }
      if (fr.individualType === IndividualType.EMPLOYEE) {
        return ind.displayName || ind.email || "";
      }
    }
    return "";
  };

  const filteredForList = useMemo(() => {
    // Temporary debugging
    if (ratingFilter !== undefined) {
      console.log("=== RATING FILTER DEBUG ===");
      console.log(
        "Rating filter value:",
        ratingFilter,
        "Type:",
        typeof ratingFilter
      );
      console.log("Total answers:", answers?.length);
      console.log(
        "Sample answers with ratings:",
        answers?.slice(0, 3).map((a) => ({
          question: a.question?.substring(0, 50),
          hasMessage: !!a.message,
          hasFeedbackRating: !!a.message?.feedbackRating,
          rating: a.message?.feedbackRating?.rating,
          ratingType: typeof a.message?.feedbackRating?.rating,
        }))
      );
    }

    const filtered = (answers || [])
      .filter((a) => {
        if (!sourceFilter) return true;
        return a.platformSource === sourceFilter;
      })
      .filter((a) => {
        if (ratingFilter === undefined) return true;
        const r = a.message?.feedbackRating?.rating;
        // Convert both values to numbers for comparison, handling potential string/number mismatches
        const ratingValue = Number(r);
        const filterValue = Number(ratingFilter);
        const matches = ratingValue === filterValue && !isNaN(ratingValue);
        console.log(
          `Rating comparison: ${r} (${typeof r}) === ${ratingFilter} (${typeof ratingFilter}) = ${matches}`
        );
        return matches;
      })
      .filter((a) => {
        if (!raterFilter) return true;
        const name = getRaterFullName(a.message?.feedbackRating);
        return name?.toLowerCase() === raterFilter?.toLowerCase();
      })
      .sort((a, b) => {
        const ad = new Date(
          a.message?.feedbackRating?.updatedAt || a.createdAt
        ).getTime();
        const bd = new Date(
          b.message?.feedbackRating?.updatedAt || b.createdAt
        ).getTime();
        return bd - ad; // most recent first
      });

    if (ratingFilter !== undefined) {
      console.log("Filtered results count:", filtered.length);
      console.log(
        "Filtered results:",
        filtered.slice(0, 2).map((a) => ({
          question: a.question?.substring(0, 50),
          rating: a.message?.feedbackRating?.rating,
        }))
      );
    }

    return filtered;
  }, [answers, ratingFilter, raterFilter, sourceFilter]);

  const ratingsForChartL30 = useMemo(() => {
    const last30Start = getDateDaysAgo(30);
    return filteredForList.filter((a) => new Date(a.createdAt) >= last30Start);
  }, [filteredForList]);

  const uniqueRaters: string[] = useMemo(() => {
    const names = new Set<string>();
    (answers || []).forEach((a) => {
      const fr: any = a.message?.feedbackRating;
      if (!fr || fr.rating === null || fr.rating === undefined) return;
      const name = getRaterFullName(fr);
      if (name) names.add(name);
    });
    return Array.from(names.values());
  }, [answers]);

  const ratingOptions = useMemo(
    () => [
      { value: "", label: "(no filter)" },
      { value: "5", label: "5 Stars" },
      { value: "4", label: "4 Stars" },
      { value: "3", label: "3 Stars" },
      { value: "2", label: "2 Stars" },
      { value: "1", label: "1 Star" },
    ],
    []
  );

  const raterOptions = useMemo(() => {
    const opts = uniqueRaters.map((name) => ({ value: name, label: name }));
    opts.unshift({ value: "", label: "(no filter)" });
    return opts;
  }, [uniqueRaters]);

  const exportRatingsCSV = () => {
    // Determine max number of documents across filtered answers based on message.sources
    const maxDocs = filteredForList.reduce((max, a) => {
      const count = a.message?.sources?.length || 0;
      return Math.max(max, count);
    }, 0);

    const header = [
      "Rater First Name",
      "Rater Last Name",
      "Rating",
      "Question",
      "Response",
      ...Array.from({ length: maxDocs }).flatMap((_, i) => [
        `Document ${i + 1} Title`,
        `Document ${i + 1} URL`,
      ]),
    ];

    const csvRows = filteredForList.map((a) => {
      const fr: any = a.message?.feedbackRating;
      let firstName = "";
      let lastName = "";
      if (fr) {
        if (fr.individualType === IndividualType.USER) {
          firstName = fr.user?.firstName || "";
          lastName = fr.user?.lastName || "";
        } else if (fr.individualType === IndividualType.EMPLOYEE) {
          const display = fr.employee?.displayName || "";
          const parts = display.split(" ");
          firstName = parts[0] || "";
          lastName = parts.slice(1).join(" ");
        }
      }
      const rating = fr?.rating?.toString() || "";

      // Build document title/url pairs dynamically from message.sources
      const sourceIds: string[] = a.message?.sources || [];
      const docPairs: string[] = [];
      for (let i = 0; i < maxDocs; i++) {
        const id = sourceIds[i];
        if (id) {
          const lr = getStoreLinkRecord(id, linkRecords || []);
          const primaryLink = lr?.links?.[0];
          docPairs.push(primaryLink?.title || "", primaryLink?.url || "");
        } else {
          docPairs.push("", "");
        }
      }

      return [
        firstName,
        lastName,
        rating,
        a.question?.replaceAll("\n", " ") || "",
        a.answer?.replaceAll("\n", " ") || "",
        ...docPairs,
      ];
    });

    const csv = [header, ...csvRows]
      .map((r) =>
        r
          .map((v) => `"${(v ?? "").toString().replaceAll('"', '""')}"`)
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "ratings.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!answers || answers.length === 0) {
    return <AskedQuestionsZero variant="ratings" />;
  }

  return (
    <div className="collection-stats-tab">
      <div className="collection-stats-summary-chart">
        <RatingsChart
          answers={ratingsForChartL30}
          endDate={new Date()}
          startDate={getDateDaysAgo(30)}
        />
      </div>

      <div className="collection-stats-filter">
        <Selector
          options={ratingOptions}
          onChange={(v) => {
            if (v === "") setRatingFilter(undefined);
            else setRatingFilter(parseInt(v, 10));
          }}
          selectedValue={ratingFilter === undefined ? "" : String(ratingFilter)}
        />

        <Selector
          options={raterOptions}
          onChange={(v) => setRaterFilter(v || undefined)}
          selectedValue={raterFilter || ""}
        />

        <SourceSelector responses={answers} onChange={setSourceFilter} />
        <div className="view-container">
          <button className="export-csv-btn" onClick={exportRatingsCSV}>
            Export CSV
          </button>
        </div>
        <ViewToggle view={view} setView={setView} />
      </div>

      <div className="collection-stats-feedback">
        <CollectionStatsQuestions
          questions={filteredForList}
          filterSource={sourceFilter}
          showFeedbackRatings={true}
          view={view}
          onRatingDelete={onRatingDelete}
        />
      </div>
    </div>
  );
};

export default RatingStats;
